import Head from "next/head";
import Link from "next/link";
import React, { useState } from "react";
import SiteShell, { APP_URL } from "@/components/SiteShell";

type LinkResult = {
  url: string;
  ok: boolean;
  status: number | null;
  finalUrl?: string;
  error?: string;
};

type CheckResponse = {
  results: LinkResult[];
  total: number;
  checked: number;
  truncated: boolean;
};

function StatusChip({ r }: { r: LinkResult }) {
  if (r.ok)
    return (
      <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-bold text-emerald-700">
        {r.status} OK
      </span>
    );
  return (
    <span className="rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-bold text-red-600">
      {r.status ?? "✕"} {r.error ?? "broken"}
    </span>
  );
}

/** Free tool: paste email HTML → link health report. */
export default function EmailLinkCheckerPage() {
  const [content, setContent] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState<CheckResponse | null>(null);

  const check = async () => {
    if (content.trim().length === 0) {
      setError("Paste your email HTML, or just a list of links.");
      return;
    }
    setBusy(true);
    setError("");
    setData(null);
    try {
      const res = await fetch("/api/tools/check-links", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error ?? "something went wrong");
      setData(json);
    } catch (e) {
      setError(e instanceof Error ? e.message : "something went wrong");
    } finally {
      setBusy(false);
    }
  };

  const broken = data?.results.filter((r) => !r.ok).length ?? 0;

  return (
    <SiteShell
      title="Free Email Link Checker — find broken links before your subscribers do"
      description="Free email link checker: paste your email HTML (or a list of URLs) and get an instant health report on every link — status codes, redirects, timeouts. Broken links kill clicks and deliverability."
    >
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "HowTo",
              name: "Check every link in a marketing email",
              step: [
                { "@type": "HowToStep", name: "Paste your email HTML or a list of URLs" },
                { "@type": "HowToStep", name: "Every link is checked live — status, redirects, timeouts" },
                { "@type": "HowToStep", name: "Fix the broken ones before you hit send" },
              ],
            }),
          }}
        />
      </Head>

      <section className="bg-gradient-to-b from-indigo-50/60 to-white">
        <div className="mx-auto max-w-4xl px-6 pb-10 pt-16 text-center">
          <div className="mx-auto mb-4 w-fit rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-[12px] font-semibold text-indigo-700">
            Free tool — no signup to try it
          </div>
          <h1 className="text-[36px] font-extrabold leading-[1.12] tracking-tight text-slate-900 sm:text-[44px]">
            Broken links{" "}
            <span className="bg-gradient-to-r from-indigo-600 to-violet-600 bg-clip-text text-transparent">
              kill campaigns.
            </span>
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-[15.5px] leading-relaxed text-slate-600">
            Paste your email HTML — or just the links — and get an instant
            health report on every URL before your subscribers find the 404
            for you.
          </p>

          <div className="mx-auto mt-7 max-w-2xl">
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={6}
              placeholder="Paste email HTML or a list of links, e.g.&#10;https://your-store.com/sale&#10;https://your-store.com/checkout"
              className="w-full rounded-2xl border border-slate-200 bg-white p-4 font-mono text-[12.5px] text-slate-800 shadow-sm outline-none placeholder:text-slate-400 focus:border-indigo-400"
            />
            <button
              onClick={check}
              disabled={busy}
              className="mt-3 w-full rounded-xl bg-indigo-600 px-6 py-3.5 text-[15px] font-semibold text-white shadow-lg shadow-indigo-200 hover:bg-indigo-500 disabled:opacity-60 sm:w-auto sm:px-10"
            >
              {busy ? "Checking every link…" : "Check my links"}
            </button>
            {error && (
              <p className="mt-2 text-[13px] font-medium text-red-500">{error}</p>
            )}
          </div>
        </div>
      </section>

      {data && (
        <section className="mx-auto max-w-3xl px-6 pb-4">
          <div className="rounded-2xl border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
              <span className="text-[14px] font-bold text-slate-800">
                {broken === 0
                  ? `All ${data.checked} links healthy ✓`
                  : `${broken} of ${data.checked} links broken`}
              </span>
              {data.truncated && (
                <span className="text-[11.5px] text-slate-400">
                  first {data.checked} of {data.total} links
                </span>
              )}
            </div>
            <ul>
              {data.results.map((r) => (
                <li
                  key={r.url}
                  className="flex items-center gap-3 border-b border-slate-50 px-4 py-2.5 last:border-0"
                >
                  <StatusChip r={r} />
                  <span className="min-w-0 flex-1 truncate font-mono text-[12px] text-slate-600">
                    {r.url}
                  </span>
                  {r.finalUrl && (
                    <span
                      title={`redirects to ${r.finalUrl}`}
                      className="shrink-0 text-[11px] text-slate-400"
                    >
                      → redirected
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-6 rounded-2xl border-2 border-indigo-200 bg-indigo-50/40 p-5 text-center">
            <p className="text-[14px] font-semibold text-slate-800">
              Links rot. This was one moment in time.
            </p>
            <p className="mx-auto mt-1 max-w-md text-[13px] leading-relaxed text-slate-600">
              FrazzleCRM re-checks every link in every automation email{" "}
              <strong>every 10 minutes</strong>, forever, and flags failures
              right on your funnel map.
            </p>
            <a
              href={`${APP_URL}/signup`}
              className="mt-4 inline-block rounded-xl bg-indigo-600 px-6 py-2.5 text-[14px] font-semibold text-white hover:bg-indigo-500"
            >
              Get always-on link monitoring — free
            </a>
          </div>
        </section>
      )}

      <section className="mx-auto max-w-4xl px-6 py-12">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
          {[
            ["Why links break", "Pages move, promos expire, typos ship. In email you can't edit after send — you only get one chance."],
            ["What we check", "Every http(s) URL: live status code, redirect chains (and where they land), timeouts. Merge-field links are skipped — they resolve per contact."],
            ["Beyond one check", "Inside FrazzleCRM the watchdog runs continuously across every automation, so a page that dies on Tuesday gets flagged on Tuesday."],
          ].map(([t, b]) => (
            <div key={t} className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
              <h3 className="text-[15px] font-bold text-slate-900">{t}</h3>
              <p className="mt-2 text-[13px] leading-relaxed text-slate-600">{b}</p>
            </div>
          ))}
        </div>
        <p className="mt-8 text-center text-[13px] text-slate-500">
          More free tools:{" "}
          <Link href="/tools/funnel-generator" className="font-medium text-indigo-600 hover:text-indigo-500">
            Funnel Map Generator
          </Link>
        </p>
      </section>
    </SiteShell>
  );
}
